import { useQuery, useQueryClient } from "@tanstack/react-query"

import type { DateRange } from "../../utils/date-ranges"

import {
  type AdminSearchesResponse,
  type AdminTopUsersResponse,
  type AdminUsageResponse,
  getAgentRuns,
  getAgentSummary,
  getDashboardSummary,
  getFeatureAdoption,
  getFeatureUsageStats,
  getModelUsage,
  getRecentEvents,
  getSearches,
  getTopUsers,
  getUploadStats,
  getUsage,
  getUsageInsights,
} from "@/api/admin-api"

import { adminQueryKeys } from "../admin-query-keys"
import { type DashboardSummaryData, GC_TIME, MEDIUM_STALE_TIME, SLOW_STALE_TIME } from "./_shared"

export function useAdminUsageQuery(range: DateRange | 7 | 30, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.usage(range),
    queryFn: () => getUsage(range),
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminTopUsersQuery(range: DateRange | 7 | 30, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.topUsers(range),
    queryFn: () => getTopUsers(range),
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminDashboardSummaryQuery(
  range: DateRange | 7 | 30,
  enabled: boolean,
  currency: "SEK" | "USD" | "EUR" = "USD"
) {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: [...adminQueryKeys.usage(range), "summary", currency],
    queryFn: async (): Promise<DashboardSummaryData> => {
      const summary = await getDashboardSummary(range, currency)

      // Also populate individual query caches to keep them in sync
      // This prevents race conditions where summary and individual queries have different data
      queryClient.setQueryData(adminQueryKeys.topUsers(range), summary.topUsers)
      queryClient.setQueryData(adminQueryKeys.searches(range), summary.searches)
      queryClient.setQueryData(adminQueryKeys.usage(range), summary.usage)

      // Return in format compatible with individual queries
      return {
        computedAt: summary.computedAt,
        usage: summary.usage,
        searches: summary.searches,
        topUsers: summary.topUsers,
      }
    },
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData): DashboardSummaryData | undefined => {
      // If we have previous data from this query, use it
      if (previousData) {
        return previousData
      }

      // Try to get data from individual queries as fallback
      // This helps when switching ranges or when summary query hasn't loaded yet
      const topUsersData = queryClient.getQueryData<AdminTopUsersResponse>(
        adminQueryKeys.topUsers(range)
      )
      const searchesData = queryClient.getQueryData<AdminSearchesResponse>(
        adminQueryKeys.searches(range)
      )
      const usageData = queryClient.getQueryData<AdminUsageResponse>(adminQueryKeys.usage(range))

      // Only return if we have all three pieces of data
      if (topUsersData && searchesData && usageData) {
        return {
          usage: usageData,
          searches: searchesData,
          topUsers: topUsersData,
        }
      }

      // Also check other common ranges as fallback (for initial load scenarios)
      // This helps when there's cached data from a different range
      // Try common presets: last7Days and last30Days
      const commonRanges: Array<DateRange | 7 | 30> = [
        { type: "preset", presetId: "last7Days" },
        { type: "preset", presetId: "last30Days" },
        7,
        30,
      ]
      for (const otherRange of commonRanges) {
        const otherRangeSummary = queryClient.getQueryData<DashboardSummaryData>([
          ...adminQueryKeys.usage(otherRange),
          "summary",
          currency,
        ])

        // Prefer other range summary if available (most complete)
        if (otherRangeSummary) {
          return otherRangeSummary
        }
      }

      // Otherwise, try to get data from a query with different currency
      // This prevents UI flicker when switching currencies
      // Only searches and topUsers are currency-independent, so we can keep those
      const otherCurrencies: Array<"SEK" | "USD" | "EUR"> = ["SEK", "USD", "EUR"]
      for (const otherCurrency of otherCurrencies) {
        if (otherCurrency !== currency) {
          const cachedData = queryClient.getQueryData<DashboardSummaryData>([
            ...adminQueryKeys.usage(range),
            "summary",
            otherCurrency,
          ])
          // Return cached data - searches and topUsers don't change with currency
          // Only usage (cost) will update when new data arrives
          if (cachedData) {
            return cachedData
          }
        }
      }
      return undefined
    },
  })
}

export function useAdminRecentEventsQuery(limit: number, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.recentEvents(limit),
    queryFn: () => getRecentEvents(limit),
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}

export function useAdminAgentRunsQuery(range: DateRange | 7 | 30, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.agentRuns(range),
    queryFn: () => getAgentRuns(range),
    enabled,
    staleTime: SLOW_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminModelUsageQuery(range: DateRange | 7 | 30, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.modelUsage(range),
    queryFn: () => getModelUsage(range),
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminFeatureAdoptionQuery(range: DateRange | 7 | 30, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.featureAdoption(range),
    queryFn: () => getFeatureAdoption(range),
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminUploadStatsQuery(range: DateRange | 7 | 30, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.uploadStats(range),
    queryFn: () => getUploadStats(range),
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminFeatureUsageStatsQuery(range: DateRange | 7 | 30, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.featureUsageStats(range),
    queryFn: () => getFeatureUsageStats(range),
    enabled,
    staleTime: SLOW_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminUsageInsightsQuery(range: DateRange | 7 | 30, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.usageInsights(range),
    queryFn: () => getUsageInsights(range),
    enabled,
    staleTime: SLOW_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminSearchesQuery(range: DateRange | 7 | 30, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.searches(range),
    queryFn: () => {
      // Convert DateRange to 7 | 30 for getSearches API
      if (typeof range === "number") {
        return getSearches(range)
      }
      // If it's a preset "last7Days" or days === 7, use 7, otherwise default to 30
      if (range.type === "preset" && range.presetId === "last7Days") {
        return getSearches(7)
      }
      if (range.type === "days" && range.days === 7) {
        return getSearches(7)
      }
      return getSearches(30)
    },
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminAgentSummaryQuery(range: DateRange | 7 | 30, enabled: boolean) {
  return useQuery({
    queryKey: adminQueryKeys.agentSummary(range),
    queryFn: () => getAgentSummary(range),
    enabled,
    staleTime: MEDIUM_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}
